import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Wallet, Sparkles, AlertTriangle, Loader2, RefreshCw,
  CreditCard, Receipt, Zap, Repeat, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const chf = (n) => 'CHF ' + (Number(n) || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf = (n) => (Number(n) || 0).toLocaleString('de-CH');
const SESSION_KEY = 'ai_budget_session';

const Bar = ({ pct }) => {
  const p = Math.min(100, Math.max(0, Number(pct) || 0));
  const color = p >= 100 ? 'bg-red-500' : p >= 80 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-slate-200" data-testid="ai-budget-bar">
      <div className={`h-full ${color} transition-all`} style={{ width: `${p}%` }} />
    </div>
  );
};

const AIBudgetPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [custom, setCustom] = useState('');
  const [buying, setBuying] = useState(false);
  const [addon, setAddon] = useState({ enabled: false, monthly_chf: 25 });
  const [reload, setReload] = useState({ enabled: false, threshold_chf: 5, topup_chf: 25, monthly_cap_chf: 100 });

  const load = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/admin/ai-budget`, { headers: headers() }),
        axios.get(`${API}/admin/ai-budget/transactions`, { headers: headers() }),
      ]);
      setData(b.data);
      setTxs(t.data.transactions || []);
      if (b.data.addon) setAddon({ enabled: !!b.data.addon.enabled, monthly_chf: b.data.addon.monthly_chf || 25 });
      if (b.data.auto_reload) {
        const a = b.data.auto_reload;
        setReload({ enabled: !!a.enabled, threshold_chf: a.threshold_chf || 5, topup_chf: a.topup_chf || 25, monthly_cap_chf: a.monthly_cap_chf || 100 });
      }
    } catch (e) {
      toast.error('KI-Guthaben konnte nicht geladen werden');
    } finally { setLoading(false); }
  }, []);

  // Rückkehr vom Checkout: Zahlungsstatus prüfen & Guthaben gutschreiben
  const checkReturn = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const sid = localStorage.getItem(SESSION_KEY);
    if (status === 'success' && sid) {
      try {
        const r = await axios.get(`${API}/admin/ai-budget/purchase/status/${sid}`, { headers: headers() });
        if (r.data?.paid) toast.success('Zahlung bestätigt – KI-Guthaben wurde gutgeschrieben');
        else toast.message('Zahlung noch nicht bestätigt. Bitte in Kürze erneut prüfen.');
      } catch (e) { /* ignore */ }
      localStorage.removeItem(SESSION_KEY);
      window.history.replaceState({}, '', '/admin/ai-budget');
    } else if (status === 'cancel') {
      toast.message('Zahlung abgebrochen');
      localStorage.removeItem(SESSION_KEY);
      window.history.replaceState({}, '', '/admin/ai-budget');
    }
  }, []);

  useEffect(() => { (async () => { await checkReturn(); await load(); })(); }, [checkReturn, load]);

  const buy = async (amount) => {
    const amt = Number(amount);
    if (!amt || amt < 5) { toast.error('Mindestbetrag CHF 5'); return; }
    setBuying(true);
    try {
      const r = await axios.post(`${API}/admin/ai-budget/purchase`,
        { amount_chf: amt, origin_url: window.location.origin },
        { headers: headers() });
      if (r.data?.checkout_url) {
        localStorage.setItem(SESSION_KEY, r.data.session_id);
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Checkout konnte nicht gestartet werden');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Zahlung konnte nicht gestartet werden');
    } finally { setBuying(false); }
  };

  const saveAddon = async (next) => {
    setAddon(next);
    try {
      await axios.put(`${API}/admin/ai-budget/addon`, next, { headers: headers() });
      toast.success('Zusatzpaket aktualisiert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };

  const saveReload = async (next) => {
    setReload(next);
    try {
      await axios.put(`${API}/admin/ai-budget/auto-reload`, next, { headers: headers() });
      toast.success(next.enabled ? 'Automatische Aufladung aktiviert' : 'Automatische Aufladung deaktiviert');
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center" data-testid="ai-budget-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  if (!data) return <div className="p-6 text-red-600" data-testid="ai-budget-error">Keine Daten.</div>;

  const presets = data.presets || [10, 25, 50, 100];
  const pct = data.pct_used || 0;
  const warn = pct >= 100 ? 'red' : pct >= 80 ? 'amber' : null;

  return (
    <div className="space-y-6" data-testid="ai-budget-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="ai-budget-back">
            <ArrowLeft className="mr-1 h-4 w-4" /> Zurück
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Wallet className="h-6 w-6 text-emerald-600" /> KI &amp; Guthaben
            </h1>
            <p className="text-sm text-slate-500">Monat {data.period_month} · Zahlungsanbieter: {data.payment_provider}</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="ai-budget-refresh"><RefreshCw className="mr-1 h-4 w-4" /> Aktualisieren</Button>
      </div>

      {data.paused && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800" data-testid="ai-budget-paused-banner">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="text-sm">
            <strong>Kostenpflichtige KI-Funktionen pausiert.</strong> Das inkludierte Kontingent ist aufgebraucht und es ist kein Zusatzguthaben vorhanden. Laden Sie Guthaben auf, um KI-Funktionen weiter zu nutzen. Die übrige Plattform bleibt uneingeschränkt nutzbar.
          </div>
        </div>
      )}
      {!data.paused && warn && (
        <div className={`flex items-center gap-3 rounded-xl border p-4 text-sm ${warn === 'red' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid="ai-budget-warn-banner">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>Sie haben {Math.round(pct)}% Ihres monatlichen KI-Kontingents verbraucht.</span>
        </div>
      )}

      {/* Guthaben-Übersicht */}
      <Card data-testid="ai-budget-summary">
        <CardHeader><CardTitle className="text-base">KI-Guthaben diesen Monat</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div data-testid="ai-budget-included"><div className="text-xs text-slate-500">Inkludiert / Monat</div><div className="text-xl font-semibold text-slate-900">{chf(data.included_chf)}</div></div>
            <div data-testid="ai-budget-consumed"><div className="text-xs text-slate-500">Verbraucht</div><div className="text-xl font-semibold text-slate-900">{chf(data.consumed_chf)}</div></div>
            <div data-testid="ai-budget-extra"><div className="text-xs text-slate-500">Zusatzguthaben (verfällt nicht)</div><div className="text-xl font-semibold text-emerald-700">{chf(data.extra_credit_chf)}</div></div>
            <div data-testid="ai-budget-remaining"><div className="text-xs text-slate-500">Verfügbar gesamt</div><div className="text-xl font-semibold text-emerald-700">{chf(data.remaining_total_chf)}</div></div>
          </div>
          <div>
            <div className="mb-1 flex justify-between text-xs text-slate-500">
              <span>Monatskontingent</span><span>{Math.round(pct)}%</span>
            </div>
            <Bar pct={pct} />
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span data-testid="ai-budget-requests">Anfragen: {nf(data.requests)}</span>
            <span data-testid="ai-budget-tokens">Tokens: {nf((data.tokens_in || 0) + (data.tokens_out || 0))}</span>
          </div>
        </CardContent>
      </Card>

      {/* Guthaben kaufen */}
      <Card data-testid="ai-budget-buy">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4" /> Zusätzliches KI-Guthaben kaufen</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <Button key={p} variant="outline" disabled={buying} onClick={() => buy(p)} data-testid={`ai-budget-preset-${p}`}>
                {buying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} CHF {p}
              </Button>
            ))}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="text-xs text-slate-500">Eigener Betrag (CHF)</label>
              <Input type="number" min="5" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="z.B. 40" className="w-40" data-testid="ai-budget-custom-input" />
            </div>
            <Button disabled={buying} onClick={() => buy(custom)} data-testid="ai-budget-custom-buy">
              {buying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />} Kaufen
            </Button>
          </div>
          <p className="text-xs text-slate-400">Gekauftes Guthaben verfällt nicht. Keine automatische Belastung ohne Ihre ausdrückliche Zustimmung.</p>
        </CardContent>
      </Card>

      {/* Zusatzpaket & Auto-Aufladung */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card data-testid="ai-budget-addon">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Repeat className="h-4 w-4" /> Monatliches Zusatzpaket</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Jeden Monat zusätzliches Guthaben</span>
              <Switch checked={addon.enabled} onCheckedChange={(v) => saveAddon({ ...addon, enabled: v })} data-testid="ai-budget-addon-toggle" />
            </div>
            <div className="flex items-end gap-2">
              <div>
                <label className="text-xs text-slate-500">Betrag / Monat (CHF)</label>
                <Input type="number" min="5" value={addon.monthly_chf} onChange={(e) => setAddon({ ...addon, monthly_chf: Number(e.target.value) })} className="w-32" data-testid="ai-budget-addon-amount" />
              </div>
              <Button variant="outline" size="sm" onClick={() => saveAddon(addon)} data-testid="ai-budget-addon-save">Speichern</Button>
            </div>
            <p className="text-xs text-slate-400">Wird zu Beginn jedes Monats als Zusatzguthaben gutgeschrieben.</p>
          </CardContent>
        </Card>

        <Card data-testid="ai-budget-autoreload">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Zap className="h-4 w-4" /> Automatische Aufladung</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Automatisch nachladen (mit Zustimmung)</span>
              <Switch checked={reload.enabled} onCheckedChange={(v) => saveReload({ ...reload, enabled: v })} data-testid="ai-budget-autoreload-toggle" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs text-slate-500">Schwelle</label><Input type="number" value={reload.threshold_chf} onChange={(e) => setReload({ ...reload, threshold_chf: Number(e.target.value) })} data-testid="ai-budget-autoreload-threshold" /></div>
              <div><label className="text-xs text-slate-500">Ladebetrag</label><Input type="number" value={reload.topup_chf} onChange={(e) => setReload({ ...reload, topup_chf: Number(e.target.value) })} data-testid="ai-budget-autoreload-topup" /></div>
              <div><label className="text-xs text-slate-500">Monatslimit</label><Input type="number" value={reload.monthly_cap_chf} onChange={(e) => setReload({ ...reload, monthly_cap_chf: Number(e.target.value) })} data-testid="ai-budget-autoreload-cap" /></div>
            </div>
            <Button variant="outline" size="sm" onClick={() => saveReload(reload)} data-testid="ai-budget-autoreload-save">Speichern</Button>
            <p className="flex items-start gap-1.5 text-xs text-slate-400"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" /> Lädt automatisch CHF {reload.topup_chf} nach, sobald das Guthaben unter CHF {reload.threshold_chf} fällt – maximal bis zum Monatslimit von CHF {reload.monthly_cap_chf}. Danach ist eine erneute Bestätigung nötig.</p>
          </CardContent>
        </Card>
      </div>

      {/* Verbrauch nach Modell */}
      <Card data-testid="ai-budget-breakdown">
        <CardHeader><CardTitle className="text-base">Verbrauch nach Modell (diesen Monat)</CardTitle></CardHeader>
        <CardContent>
          {(!data.breakdown?.by_model || data.breakdown.by_model.length === 0) ? (
            <p className="text-sm text-slate-500">Noch kein KI-Verbrauch in diesem Monat.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase text-slate-500"><th className="pb-2">Modell</th><th className="pb-2 text-right">Anfragen</th><th className="pb-2 text-right">Tokens</th><th className="pb-2 text-right">Kosten</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.breakdown.by_model.map((m) => (
                  <tr key={m.model} data-testid={`ai-budget-model-${m.model}`}>
                    <td className="py-2 text-slate-800">{m.model}</td>
                    <td className="py-2 text-right text-slate-600">{nf(m.requests)}</td>
                    <td className="py-2 text-right text-slate-500">{nf(m.tokens)}</td>
                    <td className="py-2 text-right font-medium text-slate-800">{chf(m.cost_chf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Transaktionen */}
      <Card data-testid="ai-budget-transactions">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Aufladungs-Historie</CardTitle></CardHeader>
        <CardContent>
          {txs.length === 0 ? (
            <p className="text-sm text-slate-500">Noch keine Aufladungen.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs uppercase text-slate-500"><th className="pb-2">Datum</th><th className="pb-2">Typ</th><th className="pb-2">Anbieter</th><th className="pb-2 text-right">Betrag</th><th className="pb-2 text-right">Status</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {txs.map((t) => (
                  <tr key={t.id || t.session_id} data-testid={`ai-budget-tx-${t.id || t.session_id}`}>
                    <td className="py-2 text-slate-600">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 text-slate-700">{t.type}</td>
                    <td className="py-2 text-slate-500">{t.provider}</td>
                    <td className="py-2 text-right text-slate-800">{chf(t.amount_chf)}</td>
                    <td className="py-2 text-right"><Badge variant={t.status === 'paid' ? 'default' : 'secondary'}>{t.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AIBudgetPage;
